import axios from "axios";
import env from "../environment";
import { TOKEN_KEY, refreshAccessToken } from "./authRefresh";

const api = axios.create({
  baseURL: env.API_URL,
  headers: {
    "Content-Type": "application/json;charset=utf-8",
  },
});

// PO1-2154: refresh proativo do access token. Se o JWT está a menos de 30s
// de expirar, renovamos ANTES de mandar a request — evita o 401 ruidoso no
// console (mesmo com retry automático funcionando, o browser sempre loga
// XHR 4xx como erro vermelho).
const TOKEN_EXPIRY_GUARD_SECONDS = 30;

function base64UrlDecode(str) {
  try {
    return atob(str.replace(/-/g, "+").replace(/_/g, "/"));
  } catch (_) {
    return null;
  }
}

function tokenExpiringSoon() {
  if (typeof window === "undefined") return false;
  const token = window.localStorage.getItem(TOKEN_KEY);
  if (!token) return false;
  const parts = token.split(".");
  if (parts.length !== 3) return false;
  const decoded = base64UrlDecode(parts[1]);
  if (!decoded) return false;
  try {
    const payload = JSON.parse(decoded);
    if (!payload || !payload.exp) return false;
    const nowSec = Math.floor(Date.now() / 1000);
    return payload.exp - nowSec < TOKEN_EXPIRY_GUARD_SECONDS;
  } catch (_) {
    return false;
  }
}

// PO1-1975: admin Everlog não tem embarcadorId no JWT — ele seleciona o
// embarcador no header (header bar) e isso fica em localStorage. Adicionamos
// como header X-Active-Embarcador-Id em todo request pro backend usar como
// fallback quando o JWT não tem o claim. Evita ter que mexer em 30+
// componentes que esquecem de passar `embarcadores` no query.
const COMPANY_SELECTION_KEY = "@HubEverlog:companySelection";

function readActiveEmbarcadorId() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(COMPANY_SELECTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = parsed && (parsed.empresaValue ?? parsed.empresaId ?? null);
    const num = Number(id);
    return Number.isFinite(num) && num > 0 ? num : null;
  } catch (_) {
    return null;
  }
}

api.interceptors.request.use(async (config) => {
  // Refresh proativo — evita 401 visível no console.
  // Pula pra chamadas do próprio /sessions/refresh (impossibilitaria o
  // refresh ao expirar, criando recursão).
  const url = (config && config.url) || "";
  const isAuthEndpoint = url.indexOf("/sessions") !== -1;
  if (!isAuthEndpoint && tokenExpiringSoon()) {
    try {
      const newToken = await refreshAccessToken();
      if (newToken) {
        config.headers = config.headers || {};
        delete config.headers.authorization;
        delete config.headers.Authorization;
        config.headers.Authorization = `Bearer ${newToken}`;
      }
    } catch (_) {
      // Refresh falhou — segue normalmente; o interceptor de response cuida.
    }
  }

  if (typeof FormData !== "undefined" && config?.data instanceof FormData) {
    if (typeof config.headers?.delete === "function") {
      config.headers.delete("content-type");
      config.headers.delete("Content-Type");
    } else if (config.headers) {
      delete config.headers["content-type"];
      delete config.headers["Content-Type"];
    }
  }

  const activeEmb = readActiveEmbarcadorId();
  if (activeEmb) {
    if (typeof config.headers?.set === "function") {
      config.headers.set("X-Active-Embarcador-Id", String(activeEmb));
    } else {
      config.headers = {
        ...(config.headers || {}),
        "X-Active-Embarcador-Id": String(activeEmb),
      };
    }
  }

  return config;
});

// PO1-2890: normalização da mensagem de timeout.
//
// Nenhuma das instâncias de axios do projeto define `timeout` (o default do
// axios é 0, ou seja, espera para sempre). O timeout em si NÃO entra como
// padrão global: telas de exportação são legitimamente lentas e seriam
// cortadas. Ele é aplicado por chamada, hoje só nas duas do Indicador de
// Faturamento (REQUEST_TIMEOUT_INDICADOR_MS).
//
// A normalização da mensagem, por outro lado, é global de propósito: é
// inócua e cobre qualquer chamada que venha a adotar timeout. Um timeout do
// axios chega SEM `error.response`, e as telas leem o erro do corpo primeiro,
// caindo na mensagem crua da biblioteca, em inglês ("timeout of 180000ms
// exceeded").
//
// Fica aqui, e não no interceptor de sessão (attachAuthInterceptor): aquele é
// específico de renovação de credencial e retorna cedo para tudo que não é
// falta de autorização.
export const MENSAGEM_TIMEOUT =
  "A consulta demorou mais que o esperado e foi interrompida. " +
  "Reduza o período ou aplique mais filtros e tente novamente.";

function isTimeoutError(error) {
  if (!error) return false;
  if (error.response) return false;
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") return true;
  return typeof error.message === "string" && error.message.indexOf("timeout of") === 0;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isTimeoutError(error)) {
      error.isTimeout = true;
      error.message = MENSAGEM_TIMEOUT;
    }
    return Promise.reject(error);
  }
);

export default api;
